import { useCallback } from 'react';
import { useLocalStorage } from 'usehooks-ts';
import {
  INITIAL_DSN_FILTERS,
  INITIAL_PAGINATION,
} from '../pages/dsn-list/constants/dsnListState.constants';
import { clearDsnListReturnState } from '../utils/dsnListReturnState';
import {
  DateRangePresetCustom,
  DateRangePresetDays30,
  DateRangePresetDays7,
  DateRangePresetDays90,
  DateRangeValue,
} from '../utils/dateHelpers';

// const dateSerializer = (value: Date | null): string => {
//   if (!value || !(value instanceof Date) || isNaN(value.getTime())) {
//     return '';
//   }
//   return value.toISOString();
// };

// const dateDeserializer = (value: string): Date | null => {
//   if (!value) return null;
//   const date = new Date(value);
//   return isNaN(date.getTime()) ? null : date;
// };

const arraySerializer = (value: string[]): string => {
  return JSON.stringify(value);
};

const arrayDeserializer = (value: string): string[] => {
  try {
    return JSON.parse(value);
  } catch {
    return [];
  }
};

const userIdSerializer = (value: string[] | null): string => {
  if (!value || value.length === 0) return '';
  return JSON.stringify(value);
};

const userIdDeserializer = (value: string): string[] | null => {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const serializeDateRange = (v: DateRangeValue): string => JSON.stringify(v);
const deserializeDateRange = (s: string): DateRangeValue => {
  try {
    const parsed = JSON.parse(s) as DateRangeValue;

    switch (parsed?.preset) {
      case DateRangePresetDays7:
      case DateRangePresetDays30:
      case DateRangePresetDays90:
        return {
          preset: parsed.preset,
        };
      case DateRangePresetCustom:
      default:
        return {
          preset: parsed.preset,
          start: typeof parsed.start === 'string' ? parsed.start : '',
          end: typeof parsed.end === 'string' ? parsed.end : '',
        };
    }
  } catch {
    return INITIAL_DSN_FILTERS.dateRangeValue;
  }
};

export const useDsnListLocalStorage = () => {
  const [localSelectedStatus, setLocalSelectedStatus] = useLocalStorage<string | null>(
    'dsn-status-filter',
    INITIAL_DSN_FILTERS.selectedStatus,
  );

  const [localSelectedNatures, setLocalSelectedNatures] = useLocalStorage<string[]>(
    'dsn-natures-filter',
    INITIAL_DSN_FILTERS.selectedNatures,
    { serializer: arraySerializer, deserializer: arrayDeserializer },
  );
  const [localSelectedUserIds, setLocalSelectedUserIds] = useLocalStorage<string[] | null>(
    'dsn-user-filter',
    INITIAL_DSN_FILTERS.selectedUserIds,
    { serializer: userIdSerializer, deserializer: userIdDeserializer },
  );
  const [localIsTest, setLocalIsTest] = useLocalStorage<boolean>(
    'dsn-test-mode',
    INITIAL_DSN_FILTERS.isTest,
  );
  const [searchValue, setSearchValue] = useLocalStorage<string>(
    'dsn-search-value',
    INITIAL_DSN_FILTERS.searchValue,
  );
  const [localPageSize, setLocalPageSize] = useLocalStorage<number>(
    'dsn-page-size',
    INITIAL_PAGINATION.pageSize,
  );
  const [localCurrentPage, setLocalCurrentPage] = useLocalStorage<number>(
    'dsn-current-page',
    INITIAL_PAGINATION.currentPage,
  );

  const [dateRangeValue, setDateRangeValue] = useLocalStorage<DateRangeValue>(
    'dsn-date-range',
    INITIAL_DSN_FILTERS.dateRangeValue,
    { serializer: serializeDateRange, deserializer: deserializeDateRange },
  );

  const resetFilters = useCallback(() => {
    setLocalSelectedStatus(INITIAL_DSN_FILTERS.selectedStatus);
    setLocalSelectedNatures(INITIAL_DSN_FILTERS.selectedNatures);
    setLocalSelectedUserIds(INITIAL_DSN_FILTERS.selectedUserIds);
    setSearchValue(INITIAL_DSN_FILTERS.searchValue);
    setDateRangeValue(INITIAL_DSN_FILTERS.dateRangeValue);
    setLocalCurrentPage(INITIAL_PAGINATION.currentPage);
    clearDsnListReturnState();
  }, [
    setLocalSelectedStatus,
    setLocalSelectedNatures,
    setLocalSelectedUserIds,
    setSearchValue,
    setDateRangeValue,
    setLocalCurrentPage,
  ]);

  return {
    resetFilters,
    // Filtres
    localSelectedStatus,
    setLocalSelectedStatus,
    localSelectedNatures,
    setLocalSelectedNatures,
    localSelectedUserIds,
    setLocalSelectedUserIds,
    localIsTest,
    setLocalIsTest,
    searchValue,
    setSearchValue,
    // Date range
    dateRangeValue,
    setDateRangeValue,

    // Pagination
    localPageSize,
    setLocalPageSize,
    localCurrentPage,
    setLocalCurrentPage,
  };
};
